/* ============================================
   CambiarPassword — Forced password change
   Shown on first login for admin and portal clients
   ============================================ */

import LoginShell from '@/components/auth/LoginShell'
import { useState, useMemo, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

export default function CambiarPassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const { updatePassword, isAuthenticated, isCliente, loading: authLoading } = useAuth()
  const navigate = useNavigate()

  const isPortalDomain = useMemo(() => {
    const host = window.location.hostname
    return host.startsWith('client.') || host.startsWith('portal.')
  }, [])

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--bg-body)' }}>
        <span className="login-spinner" style={{ width: 40, height: 40, borderTopColor: 'var(--primary-light)' }}></span>
      </div>
    )
  }

  if (!isAuthenticated) {
    navigate('/login', { replace: true })
    return null
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres')
      return
    }

    if (password !== confirm) {
      setError('Las contraseñas no coinciden')
      return
    }

    setLoading(true)
    try {
      await updatePassword(password)
      // Redirect based on role: admin → /, cliente → /portal
      navigate(isCliente || isPortalDomain ? '/portal' : '/', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar contraseña')
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginShell pie={<>&copy; 2026 1Conecta</>}>
      <div className="login-panel__header">
          <h1>Actualizar contraseña</h1>
          <p>Por seguridad, debes establecer una nueva para continuar.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" id="change-password-form">
          {error && (
            <div className="login-error" role="alert">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="new-password">Nueva Contraseña</label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              required
              minLength={8}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirmar Contraseña</label>
            <input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repite tu nueva contraseña"
              required
              minLength={8}
            />
          </div>

          {password.length > 0 && (
            <ul className="login-reglas">
              <li className={password.length >= 8 ? 'is-ok' : 'is-mal'}>Mínimo 8 caracteres</li>
              {confirm.length > 0 && (
                <li className={password === confirm ? 'is-ok' : 'is-mal'}>Las contraseñas coinciden</li>
              )}
            </ul>
          )}

          <button
            type="submit"
            className="portal-login-submit"
            disabled={loading || password.length < 8 || password !== confirm}
            id="change-password-submit"
          >
            {loading ? <span className="login-spinner" /> : 'Actualizar Contraseña'}
          </button>
        </form>

    </LoginShell>
  )
}
